import { Link } from "react-router-dom";
import { ArrowRight, CalendarDays, Clock, DollarSign, Flame } from "lucide-react";
import { Reveal, MaskedLine } from "../components/site/Reveal";
import { Seo } from "../components/site/Seo";
import { STAGES, getStageStatus, DPS_MEDIA } from "../lib/content";
import { trackEvent } from "../lib/analytics";

const STAGE_STATUS = getStageStatus();

const FACTS = [
  { icon: CalendarDays, k: "Program Dates", v: "Aug 28 – Nov 13 · 12 weeks + race week" },
  { icon: Clock, k: "Sessions", v: "Fri 6:00 PM · Sat 11:30 AM · 90 minutes" },
  { icon: DollarSign, k: "Pricing", v: "$20 per class · HYROX 4-pack for $60" },
  { icon: Flame, k: "Peaks For", v: "Sims Sept 13 & Oct 25 · HYROX Dallas Nov 18–22" },
];

export default function Program() {
  return (
    <div data-testid="program-page" className="pt-16">
      <Seo
        title="HYROX Dallas 12-Week Training Program — Base, Pace, Accelerate, Prime | HyroxNinja"
        description="The full 12-week HYROX Dallas training program, Aug 28 – Nov 13: Base (weeks 1–4), Pace (weeks 5–8), Accelerate (weeks 9–10), Prime (weeks 11–12) and Race Week — with the definition and application of every stage."
      />
      <section className="relative overflow-hidden">
        <img src={DPS_MEDIA.bg.program} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover opacity-15" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#070707]/70 via-[#070707]/40 to-[#070707]" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 pt-20 pb-12">
        <p className="eyebrow mb-4">Periodized For Dallas</p>
        <h1 className="font-head font-black uppercase tracking-tighter leading-[0.9] text-4xl sm:text-5xl lg:text-6xl mb-6">
          <MaskedLine delay={0.1}>The 12-Week</MaskedLine>
          <MaskedLine delay={0.25} className="text-volt">Program</MaskedLine>
        </h1>
        <p className="text-zinc-400 max-w-3xl leading-relaxed">
          In HYROX training, the terms Base, Pace, Accelerate and Prime refer to different
          training intensities and goals that help athletes prepare for the demands of the
          competition. Every Hyrox Ninja specialty class follows this structure — here is
          the breakdown of each term and exactly when it happens.
        </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-14">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
          {FACTS.map((f, i) => (
            <Reveal key={f.k} delay={i * 0.05} className="bg-[#0c0c0c] p-6 h-full">
              <div data-testid={`program-fact-${i}`}>
                <f.icon size={20} className="text-volt mb-3" />
                <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1">{f.k}</p>
                <p className="font-head font-bold uppercase text-base leading-snug">{f.v}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-16" data-testid="program-structure-section">
        <Reveal>
          <p className="eyebrow mb-3">Workout Structure</p>
          <h2 className="font-head font-extrabold uppercase tracking-tight text-2xl sm:text-3xl mb-9">
            Five stages. One race.
          </h2>
        </Reveal>
        <div className="program-timeline border border-white/10">
          {STAGES.map((s, i) => (
            <Reveal key={s.n} delay={i * 0.04}>
              <article className={`program-phase ${STAGE_STATUS?.index === i ? "is-active-stage" : ""}`} data-testid={`program-stage-${s.n}`}>
                <div className="program-phase-number">{s.n}</div>
                <div>
                  <p className="font-mono2 text-[10px] uppercase tracking-[0.22em] text-volt mb-1">{s.meta}</p>
                  <h3 className="font-head font-bold uppercase text-xl mb-2">
                    {s.name}
                    {STAGE_STATUS?.index === i && (
                      <span className={`stage-badge ${STAGE_STATUS.label === "current" ? "is-live" : ""}`} data-testid="program-stage-badge">
                        {STAGE_STATUS.label === "current" ? "Current Stage" : "Up Next"}
                      </span>
                    )}
                  </h3>
                  <dl className="program-phase-copy max-w-4xl">
                    <div>
                      <dt>Definition</dt>
                      <dd>{s.definition}</dd>
                    </div>
                    <div>
                      <dt>Application</dt>
                      <dd>{s.application}</dd>
                    </div>
                  </dl>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
        <Reveal>
          <div className="program-join-note">
            <strong>Join the program at any time.</strong>
            <span>While this is a progressive 12-week build leading up to Dallas HYROX, participants can join the training at any point during the program.</span>
          </div>
        </Reveal>
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-24">
        <Reveal>
          <div className="border border-volt/40 bg-volt/5 p-8 lg:p-10 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div>
              <h2 className="font-head font-extrabold uppercase tracking-tight text-2xl sm:text-3xl mb-2">
                Train the current stage
              </h2>
              <p className="text-zinc-400 leading-relaxed max-w-xl">
                Friday and Saturday specialty classes always target the active stage of the
                program. Book a session or preview how simulation day runs.
              </p>
            </div>
            <div className="flex flex-wrap gap-3 shrink-0">
              <Link to="/schedule#book-now" className="btn-volt" data-testid="program-book-button" onClick={() => trackEvent("booking_click", { placement: "program_page", service: "classes" })}>
                Book a Class <ArrowRight size={16} />
              </Link>
              <Link to="/simulation-format" className="btn-ghost" data-testid="program-sim-format-link">
                Simulation Format
              </Link>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
